import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, CalendarDays, User, Clock, ArrowLeft } from "lucide-react";
import { api, fileUrl } from "@/api";
import Reveal from "@/components/Reveal";
import NotFound from "@/pages/NotFound";

function formatDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

export default function NewsDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setArticle(null);
    setMissing(false);
    api.get(`/articles/by-slug/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => setMissing(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (missing) return <NotFound />;
  if (!article) return <div className="min-h-screen bg-navy-950" data-testid="article-loading" />;

  return (
    <div data-testid={`article-page-${slug}`}>
      <section className="bg-navy-950 pb-20 pt-40 lg:pt-48">
        <div className="container-rcs">
          <Reveal>
            <nav aria-label="Breadcrumb" className="mb-8 flex items-center gap-2 text-xs text-slate-400" data-testid="breadcrumb">
              <Link to="/" className="transition-colors hover:text-gold">Home</Link>
              <ChevronRight className="h-3 w-3" aria-hidden />
              <Link to="/news" className="transition-colors hover:text-gold">News & Insights</Link>
              <ChevronRight className="h-3 w-3" aria-hidden />
              <span className="text-gold line-clamp-1">{article.title}</span>
            </nav>
            <span className="eyebrow-light">{article.category}</span>
            <h1 className="mt-5 max-w-4xl font-serif text-3xl leading-tight text-ivory sm:text-4xl lg:text-5xl">
              {article.title}
            </h1>
            <div className="mt-8 flex flex-wrap items-center gap-6 text-xs text-slate-400">
              <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" aria-hidden />{formatDate(article.date)}</span>
              {article.author && <span className="inline-flex items-center gap-1.5"><User className="h-3.5 w-3.5" aria-hidden />{article.author}</span>}
              {article.readingTime && <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" aria-hidden />{article.readingTime}</span>}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="container-rcs max-w-4xl">
          {article.image && (
            <Reveal>
              <img src={fileUrl(article.image)} alt={article.title} className="aspect-[16/8] w-full rounded-2xl object-cover" data-testid="article-image" />
            </Reveal>
          )}
          <Reveal delay={0.1}>
            <div className="prose-lg mt-12 whitespace-pre-line text-base leading-loose text-slate-700" data-testid="article-body">
              {article.body || article.excerpt}
            </div>
            <Link to="/news" className="link-underline mt-14 inline-flex" data-testid="article-back">
              <ArrowLeft className="h-4 w-4" aria-hidden /> Back to News & Insights
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
